import { Loading, SearchInput } from "@alm/flow-ds/components";
import { AxiosError } from "axios";
import CardColumn from "components/CardColumn";
import DashboardLayout from "components/Layouts/DashboardLayout";
import { ListGroup, ListGroupItem } from "components/ListGroup";
import PageHeader from "components/PageHeader";
import { createAlertAction } from "data/app/appActions";
import { isAdmin } from "data/app/appSelectors";
import useCheckMountedStatus from "hooks/useCheckMountedStatus";
import usePagedData from "hooks/usePagedData";
import produce from "immer";
import api from "lib/api";
import { createErrorAlert } from "lib/api/errors";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import { createAlert } from "records/Alert";
import UploadView from "./UploadView";
import ViewCard from "./ViewCard";
import { useMobileView } from "hooks/useMobileView";
import PaginationBar from "components/PaginationBar";

export interface View {
	_id: string;
	viewLabel: string;
	description: string;
}

export default function Views() {
	const { t } = useTranslation();

	const admin = useSelector(isAdmin);

	const { _isMounted } = useCheckMountedStatus();

	const reduxDispatch = useDispatch();

	const [init, setInit] = useState(false);
	const [searchBox, setSearchBox] = useState("");
	const [searchValue, setSearchValue] = useState("");
	const [isViewAdded, setIsViewAdded] = useState(false);

	const [currentPage, setCurrentPage] = useState(1);

	const [total, setTotal] = useState<number | undefined>(0);

	const { paginationLimit } = useMobileView();

	const onChangeEvent = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			if (event.target.value !== "") {
				setSearchBox(event.target.value);
			} else {
				setSearchBox(event.target.value);
				setSearchValue(event.target.value);
			}
		},
		[]
	);

	// Pagination for views list
	const { state, setData, ready, loading, searching, patchState } =
		usePagedData();

	const { limit } = state;

	const disableOnLoad = state.loading ? "disabled" : "";

	const handleNewViewAdded = useCallback(() => {
		setIsViewAdded(true);
	}, []);

	/**
	 * Updating state to the correct offset
	 *
	 * @param id view id
	 */
	const produceOffsetPatch = useCallback(
		(id: string) =>
			produce(state, draft => {
				const index = draft.results.findIndex(
					(view: View) => view._id === id
				);
				draft.results.splice(index, 1);
				const newOffset = draft.offset - 1;
				draft.offset = newOffset;
			}),
		[state]
	);

	/**
	 * Deleting a view off the views page
	 *
	 * @param id view id
	 */
	const deleteView = useCallback(
		async (_id: string, definitionId: string) => {
			try {
				await api.view.deleteViewDefinition(definitionId, {
					handleErrors: true,
				});
				patchState(produceOffsetPatch(_id));
				reduxDispatch(
					createAlertAction(
						createAlert({
							title: t("alerts.viewDeleted.title"),
							status: "success",
							timeout: 3000,
						})
					)
				);
			} catch (error) {
				const e = error as AxiosError;
				reduxDispatch(
					createAlertAction(
						createErrorAlert(e, "deleteViewError", {
							definitionId,
						})
					)
				);
			}
		},
		[patchState, produceOffsetPatch, reduxDispatch, t]
	);

	/**
	 * Add new views to page when paged
	 */
	const pageData = useCallback(
		async (page: number) => {
			try {
				loading();
				const res = await api.view.getViews({
					offset: (page - 1) * limit,
					limit: limit,
					search: state.search,
				});
				setData(res);
				ready();
			} catch (e) {
				console.error(e);
			}
			setCurrentPage(page);
		},
		[setData, loading, ready, limit, state.search]
	);

	/**
	 * Load views on mount
	 */
	useEffect(() => {
		if (!init || isViewAdded) {
			// Load views from API
			const getViews = async () => {
				try {
					const results = await api.view.getViews({
						limit: 10,
						offset: 0,
					});
					// Only update state when the component is mounted
					if (_isMounted.current) {
						setData(results);
						setTotal(results.totalCount);
						ready();
					}
				} catch (e) {
					console.error(e);
				}
			};

			getViews();

			// Only update state when the component is mounted
			if (_isMounted.current) {
				setInit(true);
				setIsViewAdded(false);
			}
		}
	}, [_isMounted, init, isViewAdded, ready, setData, admin]);

	/**
	 * Load new list of views when search term has been changed
	 */
	useEffect(() => {
		if (init) {
			searching();
			setCurrentPage(1);
			// Load views from API with search query
			api.view
				.getViews({
					limit: 10,
					offset: 0,
					search: searchValue,
				})
				.then(results => {
					setData(results);
					setTotal(results.totalCount);
					ready();
				})
				.catch(e => console.error(e));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchValue]);

	if (!init) return <Loading className="mt-4 elem__loader--md" />;

	return (
		<DashboardLayout>
			{admin ? (
				<div className="container">
					<div className="fixed-header-container">
						<PageHeader title={t("main.views.headings.views")}>
							<UploadView onNewViewAdded={handleNewViewAdded} />
						</PageHeader>
						<div className="search-activities">
							<div className="search-bar">
								<SearchInput
									label={t("main.text.search")}
									onSearch={() => setSearchValue(searchBox)}
									onChange={onChangeEvent}
									value={searchBox}
								/>
							</div>
						</div>
					</div>
					{state.searching ? (
						<Loading className="mt-4 elem__loader--md" />
					) : state.results.length ? (
						<>
							<div
								className={`list-group-heading list-group-heading--position ${disableOnLoad}`}>
								<CardColumn width="sm">
									{t("main.views.headings.id")}
								</CardColumn>
								<CardColumn width="md">
									{t("main.views.headings.name")}
								</CardColumn>
								<CardColumn width="xl">
									{t("main.views.headings.description")}
								</CardColumn>
								<CardColumn className="view-card__menu"></CardColumn>
							</div>
							<ListGroup className="views-group">
								{state.results.map(
									(view: View, index: number) => (
										<ListGroupItem
											className={`views-card ${disableOnLoad}`}
											key={`views-card__${index}`}>
											<ViewCard
												{...view}
												deleteView={deleteView}
											/>
										</ListGroupItem>
									)
								)}
							</ListGroup>
						</>
					) : (
						!state.loading && (
							<p className="text-center">
								{t(
									searchValue.trim() !== ""
										? "main.views.text.noSearch"
										: "main.views.text.noViewDefinitions"
								)}
							</p>
						)
					)}

					{total !== undefined &&
						total > limit &&
						!state.searching && (
							<PaginationBar
								currentPage={currentPage}
								limit={limit}
								total={total}
								paginationLimit={paginationLimit}
								onPageChange={page => {
									pageData(page);
								}}
								loading={state.loading}
							/>
						)}
				</div>
			) : (
				<Redirect to="/403" />
			)}
		</DashboardLayout>
	);
}
